import { Component, computed, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ManagementCompany } from '@api/model/managementCompany';
import { ManagementOrganisation } from '@api/model/managementOrganisation';
import { ManagementApiService } from '@api/service/management.api.service';
import { FormDisplayControl } from '@oi-soft/common';
import { BaseEntityNewDirective } from '@shared/components/buttons/new-button/entity-new.directive';
import { FormControlSelectOption } from '@shared/components/form/form-control/form-control.model';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { NewButtonComponent } from "../../../../shared/components/buttons/new-button/new-button.component";
import { FormControlComponent } from "../../../../shared/components/form/form-control/form-control.component";
import { ModalFormButtonGroupComponent } from "../../../../shared/components/modals/modal/modal-button-group/modal-form-button-group/modal-form-button-group.component";
import { ModalComponent } from "../../../../shared/components/modals/modal/modal.component";

@Component({
    selector: 'app-company-new',
    imports: [ModalComponent, ReactiveFormsModule, FormControlComponent, ModalFormButtonGroupComponent, NewButtonComponent],
    templateUrl: './company-new.component.html',
    styleUrl: './company-new.component.scss'
})
export class CompanyNewComponent extends BaseEntityNewDirective<ManagementCompany> {
    
    private managementApiService = inject(ManagementApiService);

    organisations = signal<ManagementOrganisation[]|undefined>(undefined);
    organisationOptions = computed<FormControlSelectOption[]|undefined>(() => {
        return this.organisations()?.map(o => ({
            option:o.fullName,
            value:o.id
        }))
    })
    ofsCompanies = signal<ManagementCompany[]|undefined>(undefined);
    ofsCompanyOptions = computed<FormControlSelectOption[]|undefined>(() => {
        return this.ofsCompanies()?.map(o => (<FormControlSelectOption>{
            optionPrefix: o.ofsIdentifier,
            option: o.fullName,
            value: o.id,
        }))
    })

    override form = signal(this.initForm());
    
    override initForm() {
        return new FormGroup({
            organisation: new FormDisplayControl(
                'Organisation', "",
                {
                    validators: [Validators.required]
                }
            ),
            ofsCompany: new FormDisplayControl(
                'OFS Company', "",
                {
                    validators: [Validators.required]
                }
            )
        });
    }

    override getData() {
        return Promise.allSettled([
            firstValueFrom(this.getOrganisations()),
            firstValueFrom(this.getOFSCompanies())
        ])
    }
    
    getOrganisations(){
        return this.managementApiService.Management.Organisations.Get()
        .pipe(
            tap({
                next: (response) => {
                    this.organisations.set(response.result)
                }
            })
        );
    }
    
    getOFSCompanies(){
        return this.managementApiService.Management.Ofs.Companies.Get({
            params:{
                _includeSubData:true,
                filterByAvailables: [true]
            }
        })
        .pipe(
            tap({
                next: (response) => {
                    this.ofsCompanies.set(response.result)
                }
            })
        );
    }

    override submit$: () => Observable<ManagementCompany[]> = () => {

        this.swalService.fireLoading({message: "Creating Company, please wait."});
            
        return this.managementApiService.Management.Companies.Put({
            body: {
                details: [
                    {
                        organisationId: this.form().controls.organisation.value,
                        ofsIdentifier: this.form().controls.ofsCompany.value
                    }
                ]
            }
        })
        .pipe(tap(this.swalService.closeLoadingPreSubscribe()))
        .pipe(tap({
            next: (response) => {
                this.show.set(false);
                this.form().reset();
                this.swalService.fireSuccess({message: "Company created!", timer: true});
                
                if(response.length > 0){
                    this.create.emit(response[0]);
                }
            },
            error: (error) => {
                this.swalService.fireError(error);
            }
        }));

    }
    
}
